import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, inject, OnDestroy, signal, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { ImageMaskEditor } from 'src/app/classes/maps/ImageMaskEditor';
import { MapStore } from 'src/app/services/stores/map.store';
import { ButtonComponent } from 'ui-library';

export interface ImageMaskEditorData {
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
}

@Component({
  selector: 'app-mask-editor',
  templateUrl: './mask-editor.component.html',
  styleUrls: ['./mask-editor.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    TranslateModule,
    ButtonComponent,
  ],
})

export class MaskEditorComponent implements AfterViewInit, OnDestroy {
  private readonly mapStore = inject(MapStore);

  @ViewChild('maskEditorContainer', { static: true })
  private container!: ElementRef<HTMLElement>;

  readonly isLoading = signal(true);
  readonly canUndo = signal(false);
  readonly mode = signal<'delete' | 'keep'>('delete');

  private editor: ImageMaskEditor | null = null;

  async ngAfterViewInit(): Promise<void> {
    const data = this.mapStore.imageInfo();
    if (!data || this.editor) return;

    try {
      this.editor = new ImageMaskEditor({
        imageUrl: data.src,
        imageWidth: data.width,
        imageHeight: data.height,
        containerElement: this.container.nativeElement,
        initialMasks: this.mapStore.masks(),
        onStateChange: this.syncEditorState,
      });

      await this.editor.enable();

      this.syncEditorState();
      this.isLoading.set(false);
    } catch (e) {
      console.error('Mask editor init error', e);
    }
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
    this.editor = null;
  }

  setMode(mode: 'delete' | 'keep'): void {
    if (this.mode() === mode) return;

    this.mode.set(mode);
    this.editor?.setMode(mode);
  }

  undo(): void {
    this.editor?.undo();
  }

  reset(): void {
    this.editor?.reset();
  }

  cancel(): void {
    this.mapStore.setEditorMode('none');
  }

  private syncEditorState = (): void => {
    if (!this.editor) return;

    this.canUndo.set(this.editor.canUndo());

    const newMode = this.editor.mode();
    if (this.mode() !== newMode) {
      this.mode.set(newMode);
    }

    const masks = this.editor.getMasks();
    this.mapStore.saveMasks(masks);
  };
}
